import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

import { aspidaClient } from '@/lib/aspida'
import { MutationConfig, QueryConfig } from '@/lib/react-query'

import { announcementKeyFactory } from './key'

type GetAnnouncementsQuery = NonNullable<
  Parameters<typeof aspidaClient.v1.announcements.$get>[0]
>['query']

export const getAnnouncements = (query?: GetAnnouncementsQuery) => {
  return aspidaClient.v1.announcements.$get({ query })
}

type AnnouncementsData = Awaited<ReturnType<typeof getAnnouncements>>

export type UseAnnouncementsOptions = {
  query?: GetAnnouncementsQuery
  config?: QueryConfig<typeof getAnnouncements>
}

export const useAnnouncements = ({
  query,
  config,
}: UseAnnouncementsOptions = {}) => {
  const previousDataRef = useRef<AnnouncementsData>()
  const queryConfig: QueryConfig<typeof getAnnouncements> = {
    placeholderData: () => previousDataRef.current,
    cacheTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    ...config,
    queryKey: [...announcementKeyFactory.announcements(), query],
    queryFn: () => getAnnouncements(query),
  }
  const result = useQuery(queryConfig)
  if (result.isSuccess && !result.isPlaceholderData) {
    previousDataRef.current = result.data
  }
  return result
}

type UseAnnouncementsByStatusOptions = {
  query?: Omit<NonNullable<GetAnnouncementsQuery>, 'readStatus'>
  config?: QueryConfig<typeof getAnnouncements>
  readStatus: 'read' | 'unread'
}

export const useListAnnouncements = (
  options: UseAnnouncementsByStatusOptions
) =>
  useAnnouncements({
    ...options,
    query: { ...options.query, readStatus: options.readStatus },
  })

export const getAnnouncementDetail = ({
  pathParams: { announcementId },
}: Parameters<typeof announcementKeyFactory.detail>[0]) => {
  return aspidaClient.v1.announcements._announcementId(announcementId).$get()
}

export type UseAnnouncementDetailOptions = Parameters<
  typeof announcementKeyFactory.detail
>[0] & {
  config?: QueryConfig<typeof getAnnouncementDetail>
}

export const useAnnouncementDetail = ({
  pathParams,
  config,
}: UseAnnouncementDetailOptions) => {
  const queryConfig: QueryConfig<typeof getAnnouncementDetail> = {
    ...config,
    queryKey: announcementKeyFactory.detail({ pathParams }),
    queryFn: () => getAnnouncementDetail({ pathParams }),
  }
  return useQuery(queryConfig)
}

export const markAnnouncementRead = ({
  announcementId,
}: {
  announcementId: number
}) => {
  return aspidaClient.v1.announcements
    ._announcementId(announcementId)
    .read.$patch()
}

type UseMarkAnnouncementReadOptions = {
  config?: Omit<MutationConfig<typeof markAnnouncementRead>, 'onSuccess'>
}

export const useMarkAnnouncementRead = ({
  config,
}: UseMarkAnnouncementReadOptions = {}) => {
  return useMutation({
    ...config,
    mutationFn: markAnnouncementRead,
  })
}

export const bulkMarkAnnouncementsRead = ({
  announcementIds,
}: {
  announcementIds: number[]
}) => {
  return aspidaClient.v1.announcements.read.$patch({
    body: { announcementIds },
  })
}

type UseBulkMarkAnnouncementsReadOptions = {
  config?: Omit<MutationConfig<typeof bulkMarkAnnouncementsRead>, 'onSuccess'>
}

export const useBulkMarkAnnouncementsRead = ({
  config,
}: UseBulkMarkAnnouncementsReadOptions = {}) => {
  return useMutation({
    ...config,
    mutationFn: bulkMarkAnnouncementsRead,
  })
}
